import { getBackofficeUrl } from './urls';

// Mirrors apps/unity/Assets/Scripts/Data/TMXPageData.cs
export const TMXPageData = {
  Deposit: 3,
  Withdrawal: 4,
  Login: 5,
  Transfer: 6,
  AccountCreation: 7,
  AccountUpdate: 8
} as const;

// Re-exported from utils/unityStrings so the threatmetrix module retains its
// public surface; the canonical copy lives in unityStrings.
export { NO_SESSION_ID_MESSAGE } from 'utils/unityStrings';

// Sentinel sessionId used when the backend has risk assessment globally
// bypassed. Mirrors apps/payments/src/pages/amountInputPage.tsx, which sends
// the literal string 'bypassed' so the backend can recognise the case without
// the client wasting a real TMX profile.
export const BYPASSED_SESSION_ID = 'bypassed';

/**
 * Ask the backoffice whether risk assessment is globally bypassed.
 *
 * Returns false if the call fails — fail-closed on the bypass check so a
 * flaky /api/risk/config doesn't accidentally skip profiling. Mirrors
 * apps/payments behavior.
 *
 * Note: only honors the GLOBAL `bypass_risk_assessment` flag. Per-player
 * overrides are still evaluated server-side in RiskService::checkPlayerOverride
 * and do not skip client profiling.
 */
async function isRiskAssessmentBypassed(): Promise<boolean> {
  try {
    const res = await fetch(`${getBackofficeUrl()}/api/risk/config`);
    if (!res.ok) return false;
    const json = await res.json();
    return json?.bypassRiskAssessment === true;
  } catch {
    console.warn(
      '[TMX] failed to check /api/risk/config — proceeding with profiling'
    );
    return false;
  }
}

type ThreatMetrixCompletionHandler = (sessionId: string) => void;

declare global {
  interface Window {
    threatmetrix?: {
      profile: (
        domain: string,
        orgId: string,
        sessionId: string,
        pageIndex: number
      ) => void;
      // NOTE: there is deliberately no `Geofence` member here. Under the
      // RADAR_DIRECT delivery we use, geofencing is NOT a namespace on this
      // global; it is the Radar SDK, wrapped by us in utils/geofenceVerify.
      // An earlier version declared one on the strength of the vendor's written
      // doc, and it was never going to exist at runtime.
      //
      // `threatmetrix.profile()` is still called alongside the geofence check:
      // the vendor's sample links the two by passing the TMX session id to
      // Radar as `tmx:web_session_id` metadata.
    };
    // Official "Profiling Notification Completion" hook. TMX's tags.js calls
    // this with the sessionId once profiling completes. Must be installed
    // BEFORE threatmetrix.profile() is invoked.
    tmx_profiling_complete?: ThreatMetrixCompletionHandler;
  }
}

/**
 * Install a `window.tmx_profiling_complete` handler keyed to a specific sessionId
 * and return a Promise that resolves when TMX calls it (or rejects on timeout).
 *
 * Pattern ported from feature/transaction-limits-cage-online (apps/payments).
 * Preserves any pre-existing handler so multiple consumers can coexist.
 */
function waitForThreatMetrixProfile(
  sessionId: string,
  timeoutMs: number
): { cleanup: () => void; promise: Promise<void> } {
  let settled = false;
  let timeoutId: number | undefined;
  let activeHandler: ThreatMetrixCompletionHandler | undefined;
  const previousHandler = window.tmx_profiling_complete;

  const cleanup = () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    }
    if (window.tmx_profiling_complete === activeHandler) {
      if (previousHandler) {
        window.tmx_profiling_complete = previousHandler;
      } else {
        delete window.tmx_profiling_complete;
      }
    }
  };

  const promise = new Promise<void>((resolve, reject) => {
    activeHandler = (completedSessionId) => {
      previousHandler?.(completedSessionId);
      if (settled || completedSessionId !== sessionId) return;
      settled = true;
      cleanup();
      resolve();
    };
    window.tmx_profiling_complete = activeHandler;
    timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('ThreatMetrix profiling timed out'));
    }, timeoutMs);
  });

  return { cleanup, promise };
}

function resolveProfilingDomain(): string | null {
  return process.env.REACT_APP_THREATMATRIX_PROFILING_DOMAIN || 'h.online-metrix.net';
}

/**
 * Generate a fresh ThreatMetrix sessionId, kick off `threatmetrix.profile(...)`,
 * and wait for the official Profiling Notification Completion callback.
 *
 * No caching — each call is a self-contained profiling event with its own
 * sessionId, completion handler, and 30s timeout. Matches TMX's session_id
 * guidance ("a unique identifier that identifies this one event performed
 * by the user") and Unity's per-event regeneration semantics.
 *
 * On 30s timeout: fails open, returns the sessionId anyway so the backend
 * can fetch whatever fingerprint data TMX has collected by then. Matches
 * apps/payments' "continuing anyway" pattern.
 *
 * Returns null only when prerequisites are missing — env vars / toolkit /
 * playerGUID.
 */
export async function getNewSessionId(
  pageIndex: number,
  playerGUID: string,
  options: { requireProfiling?: boolean; waitForCompletion?: boolean } = {}
): Promise<string | null> {
  // `requireProfiling` restores, for one caller, the behaviour the platform had
  // before this client existed.
  //
  // THE SKIP IS A WEB-ONLY REGRESSION. Unity profiled unconditionally for its
  // entire production life -- Assets/Scripts/Managers/TMXManager.cs never reads
  // the bypass flag at all, it waits for an org id and a fingerprint server and
  // then profiles. The skip was introduced later by apps/payments as a cost
  // saving for a page that only ever wanted a risk score, and this file
  // inherited it by copying that file. The flag value in production never
  // changed; what changed is which client was running.
  //
  // That matters because profiling has a SECOND consumer now. Calling
  // `threatmetrix.profile()` is what publishes `window.Radar`, and without that
  // global the LexisNexis geofence has no SDK to ask. So a flag named for risk
  // scoring silently became a geolocation kill switch.
  //
  // Not hypothetical: PRODUCTION runs with bypassRiskAssessment=true (verified
  // 2026-10-01 against backoffice.hijack.poker/api/risk/config; test2 the same,
  // staging false). Left coupled, every production player would get
  // `sdk_unavailable`, every token would be `missing_token`, and the day
  // anyone flipped `enforce` the entire player base would be refused at once --
  // with nothing visible beforehand, because observe mode records those
  // refusals without acting on them.
  //
  // Deliberately scoped to the geofence rather than reverting to Unity's
  // unconditional profiling: the risk callers (login, cashier, account update)
  // keep today's saving, and the cashier one in particular fires on every
  // cashier open. This also raises no risk ASSESSMENT -- profiling posts device
  // data to the TMX fingerprint server, while scoring is a separate POST to
  // /api/risk that the geofence never makes.
  if (!options.requireProfiling && (await isRiskAssessmentBypassed())) {
    console.log(
      '[TMX] risk assessment bypass active — skipping profiling, returning sentinel sessionId'
    );
    return BYPASSED_SESSION_ID;
  }

  const orgId = process.env.REACT_APP_THREATMATRIX_ORG_ID;
  const domain = resolveProfilingDomain();

  if (!orgId) {
    console.error(
      '[TMX] REACT_APP_THREATMATRIX_ORG_ID is not set — restart the dev server after editing .env, or check the build env'
    );
    return null;
  }
  if (!domain) return null;
  if (!window.threatmetrix) {
    console.error(
      '[TMX] window.threatmetrix is undefined — /device-profiler.js failed to load or run (check Network tab + ad-blocker)'
    );
    return null;
  }
  if (!playerGUID) {
    console.error('[TMX] cannot generate session — missing playerGUID');
    return null;
  }

  let sid: string;
  try {
    const buf = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(`${playerGUID}_${Date.now()}`)
    );
    sid = Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (e) {
    console.error('[TMX] failed to generate sessionId:', e);
    return null;
  }

  // Install the completion handler BEFORE calling profile(). TMX's tags.js
  // checks `window.tmx_profiling_complete` synchronously when it finishes,
  // so a race here would silently drop the notification.
  const { promise } = waitForThreatMetrixProfile(sid, 30000);
  window.threatmetrix.profile(domain, orgId, sid, pageIndex);

  console.log(
    `[TMX] profile() called — domain=${domain}, sessionId=${sid.slice(0, 12)}…, pageIndex=${pageIndex}`
  );

  // `waitForCompletion: false` returns here, as soon as profile() HAS BEEN
  // CALLED but before TMX reports it finished.
  //
  // The two are not the same event and only one of them matters to the
  // geofence. `profile()` is what publishes `window.Radar` -- about a second
  // later in practice -- while the completion callback can take up to the 30s
  // timeout below. Awaiting completion first meant the player sat behind the
  // login gate for as long as that took, even though Radar had been ready
  // almost immediately. TMX completion is the RISK path's success signal, not
  // the geofence's.
  //
  // The returned sid is still correct: it is generated and handed to profile()
  // above, and the geofence only uses it as correlation metadata. Callers that
  // send it to the risk backend keep waiting, because for them a sid without a
  // completed profile is a session with no data behind it.
  if (options.waitForCompletion === false) {
    // Adopt the rejection so a timeout here cannot surface as an unhandled
    // promise rejection once nobody is awaiting it.
    promise.catch(() => {
      console.warn('[TMX] profiling did not complete within 30s (not awaited)');
    });
    return sid;
  }

  try {
    await promise;
    console.log('[TMX] profiling completed');
  } catch {
    // Fail-open on timeout — return sid anyway; backend gets degraded but
    // functional data. Matches apps/payments behavior.
    console.warn(
      '[TMX] profiling timed out after 30s — proceeding with degraded fingerprint coverage'
    );
  }

  return sid;
}
